import {
  useIsAuthenticated,
  useSession,
  useSessionUserEmail,
  useUpdatedToken,
} from 'lib/auth/auth.hooks';
import {
  postConsumerProfile,
  patchConsumerProfile,
  deleteConsumerProfile,
} from './consumer-profile-apis';
import {
  getAddress,
  getAddressById,
  getAddressByIdPublic,
  getAddressPublic,
  postAddress,
  putAddress,
  getPostcode,
  getPostcodePublic,
} from './address/address-apis';
import type {
  ConsumerPartialRequest,
  ConsumerRequest,
  ConsumerResponse,
} from './consumer.schema';
import { useContext, useEffect } from 'react';
import { getConsumerProfile } from './consumer-profile-apis';
import { ConsumerContext } from './consumer.context';
import { track } from 'lib/sdks/analytics';
import type { Address } from './address/address.schema';
import { logout as _logout } from 'lib/auth/auth';

export const useConsumerContext = () => {
  const context = useContext(ConsumerContext);

  if (context === null) {
    throw new Error(
      'useConsumerContext must be used within a ConsumerProvider',
    );
  }

  if (!context[0]) {
    throw new Error('Consumer context is not initialized.');
  }

  return context;
};

export const useConsumerEmail = () => {
  const sessionEmail = useSessionUserEmail();
  const [consumerState] = useConsumerContext();
  const { email } = consumerState;
  return email ?? sessionEmail;
};

export const useConsumerProfile = (initialValue?: ConsumerResponse) => {
  const updateAndCheckToken = useUpdatedToken();

  const [consumerState, setConsumerState] = useConsumerContext();
  const { profile } = consumerState;

  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (profile) return;

    if (initialValue) {
      setConsumerState(prev => ({
        ...prev,
        email: initialValue.email,
        profile: initialValue,
      }));
      return;
    }

    const fetchProfile = async () => {
      let token = null;

      try {
        token = await updateAndCheckToken();
      } catch (e) {
        return;
      }

      try {
        const res = await getConsumerProfile({
          token,
        });

        setConsumerState(prev => ({ ...prev, email: res.email, profile: res }));
      } catch (e) {
        setConsumerState({
          email: undefined,
          profile: null,
        });
      }
    };

    void fetchProfile();
  }, [
    initialValue,
    profile,
    setConsumerState,
    isAuthenticated,
    updateAndCheckToken,
  ]);

  return profile || initialValue;
};

type RegisterOptions = {
  source?: 'create_account' | 'auto_reg_raq' | 'auto_reg_message';
  updateSession?: boolean;
};

export const useConsumerActions = () => {
  const session = useSession();
  const checkAndUpdateToken = useUpdatedToken();

  const [, setConsumerContext] = useConsumerContext();

  const isAuthenticated = useIsAuthenticated();

  const register = async (
    consumerProfile: ConsumerRequest,
    options?: RegisterOptions,
  ) => {
    const { source, updateSession } = { updateSession: true, ...options };
    const token = await checkAndUpdateToken();

    const res = await postConsumerProfile({
      payload: {
        ...consumerProfile,
        primaryAddress: { ...consumerProfile.primaryAddress, type: 'JOB' },
      },
      token,
    });

    source && track('consumer_area.register', { source });

    const { email } = res;

    setConsumerContext(prev => ({ ...prev, email, profile: res }));
    updateSession && (await session.update());

    return res;
  };

  const getProfile = async () => {
    const token = await checkAndUpdateToken();

    const res = await getConsumerProfile({ token });
    setConsumerContext(prev => ({ ...prev, profile: res }));

    if (session.data?.user.isNewUser) {
      session.update({
        user: {
          ...session.data?.user,
          isNewUser: false,
        },
      });
    }

    return res;
  };

  const setEmail = async (email: string) => {
    setConsumerContext(
      prev =>
        ({
          ...(prev ?? {}),
          email,
        }) as any,
    );
  };

  const update = async (
    consumerProfile: ConsumerPartialRequest,
    newUserToken?: string,
  ) => {
    const token = newUserToken ?? (await checkAndUpdateToken());

    const res = await patchConsumerProfile({
      payload: consumerProfile,
      token,
    });

    track('consumer_area.account_updated');
    setConsumerContext(prev => ({ ...prev, profile: res }));
    return res;
  };

  const logout = () => {
    setConsumerContext({ profile: null });
    _logout();
  };

  const closeAccount = async () => {
    const token = await checkAndUpdateToken();

    await deleteConsumerProfile({
      token,
    });

    track('consumer_area.delete_account');

    setConsumerContext({ profile: null });
    logout();
    return;
  };

  const lookupAddress = async (query: string) => {
    try {
      const token = await checkAndUpdateToken();
      return getAddress(query, token);
    } catch (e) {
      return getAddressPublic(query);
    }
  };

  const lookupPostcode = async (query: string) => {
    try {
      const token = await checkAndUpdateToken();
      return getPostcode(query, token);
    } catch (e) {
      return getPostcodePublic(query);
    }
  };

  const retrieveAddress = async (id: string) => {
    if (!isAuthenticated) {
      return getAddressByIdPublic(id);
    }

    const token = await checkAndUpdateToken();

    return getAddressById(id, token);
  };

  const registerAddress = async (address: Address) => {
    const token = await checkAndUpdateToken();

    return postAddress({
      payload: { ...address, type: 'JOB' },
      token,
    });
  };

  const updateAddress = async ({
    id,
    address,
  }: {
    id: string;
    address: Address;
  }) => {
    const token = await checkAndUpdateToken();

    return putAddress({
      id,
      payload: { ...address, type: 'JOB' },
      token,
    });
  };

  return {
    register,
    getProfile,
    update,
    closeAccount,
    logout,
    setEmail,
    lookupAddress,
    retrieveAddress,
    registerAddress,
    updateAddress,
    lookupPostcode,
  };
};
